import React, { Suspense, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, Route, RouteComponentProps, Switch, useHistory } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { organizationApi } from "../../../../../../../../../../services/Api/Organization/organizationApi";
import { SubscriptionInvoiceApi } from "../../../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { selectOrganization } from "../../../../../../../../../../services/organizations/selectors";
import { selectIsAdmin } from "../../../../../../../../../../services/users/selectors";
import { downloadFile } from "../../../../../../../../../../utils";
import InvoicePaymentStatus from "../../components/InvoicePaymentStatus";
import InvoiceValidationErrors from "../../components/InvoiceValidationErrors";
import OrganizationInvoicesInvoicePreviewPage from "../Preview";

interface ListState {
	resource?: Record<string, any>;
	matchParams: RouteComponentProps;
}

const listName = "invoice";
const resourceType = "invoice";

const List = ({ resource, matchParams }: ListState) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [items, setItems] = useState<SubscriptionInvoiceApi[]>([]);
	const organization = useSelector(selectOrganization);
	const [errors, setErrors] = useState<ApiError[]>([]);
	const queryParams = new URLSearchParams(window.location.search);
	const [observedInvoice, setObservedInvoice] = useState<string | null>(queryParams.get("invoice_payment_progress"));
	const [startCheckingPaymentStatus, setStartCheckingPaymentStatus] = useState<boolean>(
		!!queryParams.get("invoice_payment_progress")
	);
	const intervalRef = useRef<number>(0);
	const segmentContextValue = React.useContext(SegmentContext);
	const history = useHistory();
	const [loadingItems, setLoadingItems] = useState(true);
	const isAdmin = useSelector(selectIsAdmin);

	useEffect(() => {
		setObservedInvoice(queryParams.get("invoice_payment_progress"));
	}, [queryParams.get("invoice_payment_progress")]);

	useEffect(() => {
		if (intervalRef.current) {
			window.clearInterval(intervalRef.current);
		}
		intervalRef.current = window.setInterval(() => {
			updateBadItems();
		}, 10000);

		return () => {
			window.clearInterval(intervalRef.current);
		};
	}, [items]);

	if (!resource) return null;
	const data = resource.read();

	const closePreview = () => {
		history.push(`/${organization.id}/subscriptions/invoices`);
	};

	const updateBadItems = () => {
		const itemsNoFactures = items.filter((i) => !i.invoice_id && i.payment_status === "PAID");
		itemsNoFactures.forEach(async (i) => {
			const res = await getInvoiceDataByProformaId(i.proforma_id);
			if (res && JSON.stringify(res) !== JSON.stringify(i)) {
				items[items.findIndex((item) => item.proforma_id === i.proforma_id)] = res;
				setItems([...items]);
			}
		});
	};

	const exportProformaToPdf = async (id: string) => {
		await downloadFile(
			() => organizationApi.getSubscriptionProformaPdf(id),
			`${t("common.word.proforma")}_${id}.pdf`,
			addFlash
		);
	};

	const exportInvoiceToPdf = async (id: string) => {
		await downloadFile(
			() => organizationApi.getSubscriptionInvoicePdf(id),
			`${t("common.word.invoice")}_${id}.pdf`,
			addFlash
		);
	};

	const getInvoiceDataByProformaId = async (proformaId: string) => {
		const params: Record<string, any> = {
			f: btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}proforma_id|e=${proformaId}`))),
		};
		if (config.fetch) {
			const res = await organizationApi.getSubscriptionInvoices(params);
			return res[0] || null;
		}
	};

	const fetchInvoices = async () => {
		if (config.fetch) {
			return organizationApi.getSubscriptionInvoices();
		}
	};

	const onGenerateInvoice = async (invoiceId: string) => {
		try {
			await organizationApi.generateInvoice(invoiceId);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const isInvoiceUnpaid = (paymentStatus: string) =>
		paymentStatus === "OPEN" || paymentStatus === "ERROR" || paymentStatus === "PROGRESS";

	let config = {
		fields: [
			{
				id: "number",
				name: t("lib:common.word.number"),
				render: (item: SubscriptionInvoiceApi) => (
					<Link
						to="#"
						onClick={() => {
							if (item.invoice_id) exportInvoiceToPdf(item.proforma_id);
							else exportProformaToPdf(item.proforma_id);
						}}
					>
						{item.invoice_id ? item.invoice_number : item.proforma_number}
					</Link>
				),
				renderExport: (item: SubscriptionInvoiceApi) =>
					item.invoice_id ? item.invoice_number : item.proforma_number,
			},
			{
				id: "proforma_created_at",
				name: t("modules.invoice.field.proforma_created_at.title"),
				render: (item: SubscriptionInvoiceApi) => {
					return isInvoiceUnpaid(item.payment_status) ? (
						<Link to={`/${organization.id}/subscriptions/invoices/${item.proforma_id}`}>
							{FormatDate(item?.proforma_created_at)}
						</Link>
					) : (
						FormatDate(item?.proforma_created_at)
					);
				},
				renderExport: (item: SubscriptionInvoiceApi) => FormatDate(item?.proforma_created_at),
			},
			{
				id: "issued_at",
				name: t("modules.invoice.field.invoice_date.title"),
				render: (item: SubscriptionInvoiceApi) => FormatDate(item?.paid_at),
			},
			{
				id: "payment_status",
				name: t("lib:common.word.status"),
				render: (item: SubscriptionInvoiceApi) => {
					if (observedInvoice === item.proforma_id) {
						return (
							<span
								style={{ width: "fit-content" }}
								className={"d-flex align-items-center badge bg-primary"}
							>
								<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
								{t("modules.invoice.field.payment_in_progress.title")}
							</span>
						);
					}
					return (
						<div>
							<FormatResourceStatus
								status={item.payment_status === "OPEN" ? "NOT_PAID" : item.payment_status}
							/>
							<div>
								{item.last_paid_at &&
									t("modules.invoice.field.last_paid_at.title") + FormatDate(item.last_paid_at)}
							</div>
							<div>
								{item.payment_error && t("modules.invoice.field.reason.title") + item.payment_error}
							</div>
						</div>
					);
				},
			},
			{
				id: "to_paid",
				name: t("modules.invoice.field.amount.title"),
				disableSorting: true,
				render: (item: SubscriptionInvoiceApi) => (
					<div className="d-flex flex-column">
						<span>{FormatMoney(item?.total_without_credits_and_adjustments_money)}</span>
						<small className="text-muted">
							{t("modules.invoice.field.paid.title")}
							{FormatMoney(item?.paid_money)}
						</small>
						<small className="text-muted">
							{t("modules.invoice.field.to_paid.title")}
							{FormatMoney(item?.due_money)}
						</small>
					</div>
				),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
				renderExport: (item: SubscriptionInvoiceApi) => {
					return `${FormatMoney(item?.total_without_credits_and_adjustments_money)}/ ${FormatMoney(
						item?.paid_money
					)}/ ${FormatMoney(item?.due_money)}`;
				},
			},
		],
		actions: [
			{
				name: t("modules.invoice.action.payment_in_progress.title"),
				click: () => {},
				visible: (item: SubscriptionInvoiceApi) => observedInvoice === item.proforma_id,
			},
			{
				name: `${t("common.action.pay")}`,
				link: (item: SubscriptionInvoiceApi) =>
					`/${organization.id}/subscriptions/invoices/${item.proforma_id}`,
				visible: (item: SubscriptionInvoiceApi) =>
					isInvoiceUnpaid(item.payment_status) && observedInvoice !== item.proforma_id,
			},
			{
				name: (
					<div className="d-flex align-items-center">
						<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
						<span>{t("modules.invoice.field.generating_invoice.title")}</span>
					</div>
				),
				click: () => {},
				visible: (item: SubscriptionInvoiceApi) =>
					!item.invoice_id &&
					item.payment_status === "PAID" &&
					item.total_without_credits_and_adjustments_money.amount !== 0,
			},
			{
				name: t("modules.invoice.action.generate_invoice.title"),
				click: (item: SubscriptionInvoiceApi) => onGenerateInvoice(item.proforma_id),
				visible: (item: SubscriptionInvoiceApi) =>
					!item.invoice_id &&
					item.payment_status === "PAID" &&
					item.total_without_credits_and_adjustments_money.amount !== 0 &&
					isAdmin,
			},
			{
				name: t("modules.invoice.action.download_invoice.title"),
				click: (item: SubscriptionInvoiceApi) => exportInvoiceToPdf(item.proforma_id),
				visible: (item: SubscriptionInvoiceApi) => item.invoice_id && item.payment_status === "PAID",
			},
			{
				name: t("modules.invoice.action.download_proforma.title"),
				click: (item: SubscriptionInvoiceApi) => exportProformaToPdf(item.proforma_id),
			},
		],
		filters: [
			{
				id: "due_date",
				type: "date",
				name: t("modules.invoice.field.invoice_date.title"),
			},
		],
		selectedColumns: ["number", "proforma_created_at", "issued_at", "payment_status", "to_paid"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: "Wszystkie",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params?: Record<string, any>, sourceToken?: CancelTokenSource) => {
			return organizationApi.getSubscriptionInvoices(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.invoice.header.title"),
			filename: t("modules.invoice.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 2,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	const handleUpdateItems = async (status: string) => {
		if (status !== "PROGRESS") {
			setObservedInvoice(null);
			const fetchedItems = await config.fetch?.({});
			setItems(fetchedItems);
		}
	};

	const currentInvoiceCheckingForStatus = items.find((f) => f.proforma_id === observedInvoice);

	return (
		<>
			{observedInvoice && currentInvoiceCheckingForStatus && startCheckingPaymentStatus && (
				<InvoicePaymentStatus handleUpdate={handleUpdateItems} invoice={currentInvoiceCheckingForStatus} />
			)}
			{errors.length ? <InvoiceValidationErrors errors={errors} /> : null}
			<ListData
				config={config}
				data={items}
				onFetch={(items: SubscriptionInvoiceApi[]) => {
					setItems(items);
					setLoadingItems(false);
				}}
			/>
			<Switch>
				<Route
					path={`${matchParams.match.url}/:proforma_id`}
					render={({ match }) => (
						<OrganizationInvoicesInvoicePreviewPage
							match={match}
							loc={[]}
							setErrors={setErrors}
							setStartCheckingPaymentStatus={setStartCheckingPaymentStatus}
							setObservedInvoice={setObservedInvoice}
							fetchInvoices={fetchInvoices}
							loadingItems={loadingItems}
							cardPaymentInProgress={!!currentInvoiceCheckingForStatus}
							closePreview={closePreview}
						/>
					)}
				/>
			</Switch>
		</>
	);
};

const OrganizationInvoicesIndexPage = (props: RouteComponentProps) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.invoice.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<>
			{!isMobile && <Header title={t("modules.invoice.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} matchParams={props} />
			</Suspense>
		</>
	);
};

export default OrganizationInvoicesIndexPage;
